import type {
   CompanyTotalsData,
   PeriodTotals,
   Breakdown,
} from "@laborchart-modules/lc-core-api/dist/api/totals/get-company-totals";
import type { ColumnDefinition, NestedColumnDefinition } from "@procore/data-table";

export type BreakdownRow = {
   id: string;
   category: string;
   subcategory: string;
   isDelta: boolean;
   [period: string]: string | number | boolean;
   total: number;
};

export enum Category {
   Assigned = "assigned",
   Requested = "requested",
   TimeOffMaintenance = "time_off_maintenance",
   Total = "total",
   Delta = "delta",
}

export enum Subcategory {
   Labor = "labor",
   Equipment = "equipment",
}

export const CATEGORY_ORDER = [
   Category.Assigned,
   Category.Requested,
   Category.TimeOffMaintenance,
   Category.Total,
   Category.Delta,
] as const;

export function extractPeriods(data: CompanyTotalsData): string[] {
   const periodSet = new Set<string>();
   const allArrays: PeriodTotals[][] = [
      data.assignments.labor,
      data.assignments.equipment,
      data.requests.labor,
      data.requests.equipment,
      data.time_off_and_maintenance.labor,
      data.time_off_and_maintenance.equipment,
   ];
   for (const arr of allArrays) {
      for (const item of arr) {
         periodSet.add(item.period);
      }
   }
   return Array.from(periodSet).sort();
}

function periodArrayToMap(arr: PeriodTotals[]): Record<string, number> {
   const map: Record<string, number> = {};
   for (const item of arr) {
      map[item.period] = item.resource_count;
   }
   return map;
}

function buildRow(
   id: string,
   category: Category,
   subcategory: Subcategory,
   isDelta: boolean,
   periodValues: Record<string, number>,
   periods: string[],
): BreakdownRow {
   const row: BreakdownRow = { id, category, subcategory, isDelta, total: 0 };
   for (const p of periods) {
      const val = periodValues[p] ?? 0;
      row[p] = val;
      row.total += val;
   }
   return row;
}

export function buildBreakdownRows(data: CompanyTotalsData): BreakdownRow[] {
   const periods = extractPeriods(data);

   const assignLabor = periodArrayToMap(data.assignments.labor);
   const assignEquip = periodArrayToMap(data.assignments.equipment);
   const reqLabor = periodArrayToMap(data.requests.labor);
   const reqEquip = periodArrayToMap(data.requests.equipment);
   const toLabor = periodArrayToMap(data.time_off_and_maintenance.labor);
   const toEquip = periodArrayToMap(data.time_off_and_maintenance.equipment);

   // Compute Total per period
   const totalLaborMap: Record<string, number> = {};
   const totalEquipMap: Record<string, number> = {};
   for (const p of periods) {
      totalLaborMap[p] = (assignLabor[p] ?? 0) + (reqLabor[p] ?? 0) + (toLabor[p] ?? 0);
      totalEquipMap[p] = (assignEquip[p] ?? 0) + (reqEquip[p] ?? 0) + (toEquip[p] ?? 0);
   }

   // Compute Delta per period
   const deltaLaborMap: Record<string, number> = {};
   const deltaEquipMap: Record<string, number> = {};
   for (const p of periods) {
      deltaLaborMap[p] = data.total_people - totalLaborMap[p];
      deltaEquipMap[p] = data.total_equipment - totalEquipMap[p];
   }

   return [
      // Assigned group
      buildRow("assigned-labor", Category.Assigned, Subcategory.Labor, false, assignLabor, periods),
      buildRow(
         "assigned-equipment",
         Category.Assigned,
         Subcategory.Equipment,
         false,
         assignEquip,
         periods,
      ),
      // Requested group
      buildRow("requested-labor", Category.Requested, Subcategory.Labor, false, reqLabor, periods),
      buildRow(
         "requested-equipment",
         Category.Requested,
         Subcategory.Equipment,
         false,
         reqEquip,
         periods,
      ),
      // Time Off & Maintenance group
      buildRow(
         "timeoff-labor",
         Category.TimeOffMaintenance,
         Subcategory.Labor,
         false,
         toLabor,
         periods,
      ),
      buildRow(
         "timeoff-equipment",
         Category.TimeOffMaintenance,
         Subcategory.Equipment,
         false,
         toEquip,
         periods,
      ),
      // Total group
      buildRow("total-labor", Category.Total, Subcategory.Labor, false, totalLaborMap, periods),
      buildRow(
         "total-equipment",
         Category.Total,
         Subcategory.Equipment,
         false,
         totalEquipMap,
         periods,
      ),
      // Delta group
      buildRow("delta-labor", Category.Delta, Subcategory.Labor, true, deltaLaborMap, periods),
      buildRow(
         "delta-equipment",
         Category.Delta,
         Subcategory.Equipment,
         true,
         deltaEquipMap,
         periods,
      ),
   ];
}

export function formatPeriodHeader(period: string, breakdown: Breakdown, locale: string): string {
   if (breakdown === "monthly") {
      // period is "YYYY-MM" — e.g. "2026-02" -> "Feb 2026"
      const [year, month] = period.split("-");
      const date = new Date(Number(year), Number(month) - 1, 1);
      return date.toLocaleDateString(locale, { month: "short", year: "numeric" });
   }
   if (breakdown === "bimonthly") {
      // period is "YYYY-MM" — show 2-month span, e.g. "2026-01" -> "Jan–Feb 2026"
      const [year, month] = period.split("-");
      const startMonth = new Date(Number(year), Number(month) - 1, 1);
      const endMonth = new Date(Number(year), Number(month), 1);
      const startLabel = startMonth.toLocaleDateString(locale, { month: "short" });
      const endLabel = endMonth.toLocaleDateString(locale, { month: "short" });
      const yearLabel = endMonth.toLocaleDateString(locale, { year: "numeric" });
      return `${startLabel}\u2013${endLabel} ${yearLabel}`;
   }
   if (breakdown === "quarterly") {
      // period is "YYYY-MM" — derive quarter, e.g. "2026-01" -> "Q1 2026"
      const [year, month] = period.split("-");
      const quarter = Math.ceil(Number(month) / 3);
      return `Q${quarter} ${year}`;
   }
   // daily/weekly: "YYYY-MM-DD" -> "Feb 10"
   const [year, month, day] = period.split("-");
   const date = new Date(Number(year), Number(month) - 1, Number(day));
   return date.toLocaleDateString(locale, { month: "short", day: "numeric" });
}

export function getGroupKey(period: string, breakdown: Breakdown): string {
   if (breakdown === "daily" || breakdown === "weekly") {
      // "YYYY-MM-DD" -> "YYYY-MM"
      return period.slice(0, 7);
   }
   // monthly/bimonthly/quarterly: "YYYY-MM" -> "YYYY"
   return period.slice(0, 4);
}

export function getGroupLabel(groupKey: string, breakdown: Breakdown, locale: string): string {
   if (breakdown === "daily" || breakdown === "weekly") {
      // groupKey is "YYYY-MM" -> localized month name + year
      const [year, month] = groupKey.split("-");
      const date = new Date(Number(year), Number(month) - 1, 1);
      return date.toLocaleDateString(locale, { month: "long", year: "numeric" });
   }
   // groupKey is "YYYY" -> "2026"
   return groupKey;
}

export function groupPeriodColumns(
   periodColumns: ColumnDefinition[],
   periods: string[],
   breakdown: Breakdown,
   locale: string,
): Array<ColumnDefinition | NestedColumnDefinition> {
   const groups: Array<{ key: string; columns: ColumnDefinition[] }> = [];

   for (let i = 0; i < periods.length; i++) {
      const key = getGroupKey(periods[i], breakdown);
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.key === key) {
         lastGroup.columns.push(periodColumns[i]);
      } else {
         groups.push({ key, columns: [periodColumns[i]] });
      }
   }

   return groups.map(({ key, columns }) => ({
      groupId: key,
      headerName: getGroupLabel(key, breakdown, locale),
      marryChildren: true,
      children: columns,
   }));
}
